import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import { LeadsService } from '@/data/leads.service';
import { supabase } from '@/integrations/supabase/client';
import { buildWhatsAppLink } from '@/core/decision-engine/eliteEngine';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

/**
 * Hook to send WhatsApp message (Level 1: Click-to-WhatsApp)
 * Opens wa.me link with prefilled message and logs event
 */
export function useSendWhatsApp() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      leadId,
      phone,
      message,
      templateId,
    }: {
      leadId: string;
      phone: string;
      message: string;
      templateId?: string;
    }) => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      if (!user?.id) throw new Error('No user');

      // Build WhatsApp link with prefilled message
      const whatsappLink = buildWhatsAppLink(phone, message);

      // Open WhatsApp
      window.open(whatsappLink, '_blank');

      // Update lead last outbound: last_contacted_at and last_message_at (Keep Warm cooldown resets)
      const now = new Date().toISOString();
      try {
        await LeadsService.updateLead({
          id: leadId,
          workspace_id: activeWorkspaceId,
          last_contacted_at: now,
          last_message_at: now,
        } as any);
      } catch (error) {
        logger.warn('[useSendWhatsApp] Failed to update lead timestamps', error);
      }

      // Log event (template_id stored in column if present, and in meta for backward compatibility)
      try {
        await supabase.from('lead_activities').insert({
          workspace_id: activeWorkspaceId,
          lead_id: leadId,
          user_id: user.id,
          type: 'whatsapp',
          channel: 'whatsapp',
          notes: message,
          template_id: templateId ?? null,
          meta: {
            templateId: templateId ?? undefined,
            action: 'click_to_whatsapp',
          },
        } as any);
      } catch (error: any) {
        if (error?.code !== '42P01') {
          logger.error('[useSendWhatsApp] Failed to log activity', error);
        }
      }

      return { whatsappLink };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['keep-warm-queue'], exact: false });
    },
  });
}

/**
 * Log WhatsApp outbound (update lead + log activity) without opening the link.
 * Use from TemplatePickerDialog when the picker opens wa.me itself.
 */
export function useLogWhatsAppOutbound() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      leadId,
      phone,
      message,
      templateId,
    }: {
      leadId: string;
      phone: string;
      message: string;
      templateId?: string;
    }) => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      if (!user?.id) throw new Error('No user');

      const now = new Date().toISOString();
      try {
        await LeadsService.updateLead({
          id: leadId,
          workspace_id: activeWorkspaceId,
          last_contacted_at: now,
          last_message_at: now,
        } as any);
      } catch (error) {
        logger.warn('[useLogWhatsAppOutbound] Failed to update lead timestamps', error);
      }

      try {
        await supabase.from('lead_activities').insert({
          workspace_id: activeWorkspaceId,
          lead_id: leadId,
          user_id: user.id,
          type: 'whatsapp',
          channel: 'whatsapp',
          notes: message,
          template_id: templateId ?? null,
          meta: {
            templateId: templateId ?? undefined,
            action: 'click_to_whatsapp',
          },
        } as any);
      } catch (error: any) {
        if (error?.code !== '42P01') {
          logger.error('[useLogWhatsAppOutbound] Failed to log activity', error);
        }
      }

      return {};
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['keep-warm-queue'], exact: false });
    },
  });
}

